import * as React from "react";
import * as Stores from "client/stores";
import * as Frontend from "kmmp/frontend";
import { useLocation } from "wouter";
import { Paths } from "shared/paths";
import PageHeader from "client/components/page-header";
import { uuid } from "shared/uuid";
import { VisualSampleFramePreview } from "./visual_sample_frame_selector";
import { useSampleReducer } from "./reducer";
import { Page } from "./enums";
import { Controls } from "./controls";
import { SampleQuantitiesRecord } from "./types";
import { getStateValidationError } from "./validation";

type PageConfig = {
    type: "packages" | "frames" | "corners";
    title: string;
    quantities: SampleQuantitiesRecord;
}

type PageConfigurations = { [K in Page]: PageConfig };

export function SamplePortraitsPage(): React.ReactElement {
    const [state, dispatch] = useSampleReducer({});
    const [_, setLocation] = useLocation();

    const onClickPrimaryButton = () => {
        if (state.page !== Page.Confirm && state.page !== Page.Corners)
            return dispatch({ type: "advance" });

        // Validate as if we're on the Confirm page
        const validationResult = getStateValidationError(Page.Confirm, state);

        if (validationResult.isSome())
            return dispatch({ type: "set_final_validation_error", message: validationResult.get() });
        else
            dispatch({ type: "clear_final_validation_error" });

        Stores.Cart.addSamplePortraitsToCart({
            uuid: uuid(),
            type: "sample-portraits",
            customization: {},
            qty: {
                packages: state.packages.packages,
                corners: state.corners.corners,
                frames: state.frames.frames,
            },
            source: "customize",
            purchaseOrderId: state.purchaseOrderId,
            originalOrderId: null,
        });

        // Sample frames skip the cart name page (they have no decedent), but still go through
        // shipping before the details step.
        setLocation(Stores.Auth.companySettings.siteType === "ecommerce" ? Paths.cart.index : Paths.cart.shipping);
    };

    const onClickSecondaryButton = () => {
        dispatch({ type: "reverse" });
    };

    const onSetQuantity = React.useCallback((frame: Frontend.SampleFrameName, quantity: number) => {
        dispatch({ type: "set_frame_quantity", quantity: quantity, frame: frame, page: state.page });
    }, [dispatch, state.page]);

    const onSetPurchaseOrderId = React.useCallback((purchaseOrderId) => {
        dispatch({ type: "set_purchase_order_id", value: purchaseOrderId });
    }, [dispatch]);

    const onSetInstructions = React.useCallback((value: string) => {
        dispatch({ type: "set_instructions", instructions: value });
    }, [dispatch]);

    const pageType: PageConfigurations = {
        [Page.Packages]: {
            type: "packages",
            title: "Sample Packages",
            quantities: state.packages.packages,
        },
        [Page.Frames]: {
            type: "frames",
            title: "Sample Frames",
            quantities: state.frames.frames,
        },
        [Page.Corners]: {
            type: "corners",
            title: "Sample Corners",
            quantities: state.corners.corners,
        },
        [Page.Confirm]: {
            type: "corners",
            title: "Sample Portraits",
            quantities: state.corners.corners,
        },
    };
    const pageConfig = pageType[state.page];

    return (
        <section id="sample-portraits">
            <PageHeader title={`Order Samples`} />
            <div className="section white pure-g center-children">
                <div className="pure-u-22-24">
                    <div className="pure-u-13-24 col-sm-pull-5">
                        <VisualSampleFramePreview type={pageConfig.type} />
                    </div>
                    <div className="pure-u-2-24" />
                    <div className="pure-u-9-24 col-sm-push-7">
                        <div className="product main" itemScope={true} itemType="http://schema.org/Product">
                            <h1 className="heading" itemProp="name">
                                {pageConfig.title}
                            </h1>
                            <hr style={{ margin: "25px 0" }} />
                            <Controls
                                type={pageConfig.type}
                                error={state.error ?? null}

                                quantities={pageConfig.quantities}
                                onSetQuantity={onSetQuantity}

                                onSetPurchaseOrderId={onSetPurchaseOrderId}
                                purchaseOrderId={state.purchaseOrderId}
                                showPurchaseOrderIdInput={state.page === Page.Packages && Stores.Auth.requireCoupaPurchaseOrder}

                                instructions={state.packages.instructions}
                                onSetInstructions={onSetInstructions}
                                showInstructionsInput={state.page === Page.Packages}

                                onPrimaryButtonClick={onClickPrimaryButton}
                                onSecondaryButtonClick={onClickSecondaryButton}
                                primaryButtonText={"Continue"}
                                secondaryButtonText={state.page !== Page.Packages ? "Back" : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

